import type { Dispatch, SetStateAction, JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocalStorage, useToast } from '@wistia/vhs';
import type { Nilable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import { TEAM_COMMENT_POSTED } from '~/components/Footer/OnboardingFooter/v3/hooks/useRefetchOnMessageEvent';
import {
  useCreateAnonymousAudienceCommentMutation,
  useCreateAnonymousTeamCommentMutation,
  useCreateAudienceCommentMutation,
  useCreateTeamCommentMutation,
  useDeleteAudienceCommentMutation,
  useDeleteTeamCommentMutation,
  useUpdateAudienceCommentMutation,
  useUpdateTeamCommentMutation,
} from './__gql/Comments.gql.gen';
import { generateAnonInitials } from './utils';
import type { SelectableContact } from './types';
import { MediaStoreSelectors, useMediaStore } from '../Store/MediaStore';

export type CommentingViewsType = 'Audience' | 'Team';

export interface ReactMentionsContact {
  id: string;
  display: string;
  name?: string;
  email: string;
}

interface CommentContextData {
  activeCommentId?: string;
  commentingView: CommentingViewsType;
  setCommentingView: Dispatch<SetStateAction<CommentingViewsType>>;
  deleteComment: (commentId: string) => void;
  createComment: (
    body: string,
    parentId: string | null,
    mediaTimestamp: number | null,
    mediaId: string,
    name?: Nilable<string>,
  ) => void;
  updateComment: (body: string, commentId: string) => void;
  onCommentClick?: (commentId: string | null, timestamp?: number | null) => void;
  isAnonymousMedia: boolean;
  anonUserName: string | null | undefined;
  setAnonUserName: Dispatch<SetStateAction<string | null | undefined>>;
  shouldAllowReplies: boolean;
  anonInitials: string;
  formattedContactsForTagging: ReactMentionsContact[];
  shouldUseLegacyPlayerApi: boolean;
  showCommentTabs: boolean;
}

export const CommentContext = createContext<CommentContextData | null>(null);

export const commentingViews: { audience: 'Audience'; team: 'Team' } = {
  audience: 'Audience',
  team: 'Team',
};

const onCompleted = (id: string | undefined) => {
  if (isNotNil(id)) {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};

const formattedContacts = (
  contacts: (SelectableContact | null | undefined)[] | null,
): ReactMentionsContact[] => {
  const filteredContacts = contacts?.filter(isNotNil);
  return filteredContacts?.map((contact: SelectableContact) => ({
    id: contact.id,
    display: isNotNil(contact.name) ? `${contact.name} ${contact.email}` : contact.email,
    name: contact.name,
    email: contact.email,
  })) as ReactMentionsContact[];
};

export const CommentContextProvider = ({
  children,
  activeCommentId,
  defaultCommentingView,
  onCommentClick,
  isAnonymousMedia = false,
  contactsForTeamTags = [],
  shouldUseLegacyPlayerApi = false,
  showCommentTabs = false,
}: {
  children: JSX.Element | ReactNode;
  activeCommentId?: string;
  defaultCommentingView?: CommentingViewsType;
  onCommentClick?: (commentId: string, timestamp?: number) => void;
  isAnonymousMedia?: boolean;
  contactsForTeamTags?: (SelectableContact | null | undefined)[] | null;
  shouldUseLegacyPlayerApi?: boolean;
  showCommentTabs?: boolean;
}): JSX.Element => {
  const showToast = useToast();
  const [commentingView, setCommentingView] = useState<CommentingViewsType>(
    defaultCommentingView ?? commentingViews.audience,
  );

  const commentingSort = useMediaStore(MediaStoreSelectors.getCommentingSort);

  const [storedUserName, setStoredUserName] = useLocalStorage('WISTIA_COMMENTER', null);
  const [anonUserName, setAnonUserName] = useState<string | null | undefined>(storedUserName);

  const shouldAllowReplies = !isAnonymousMedia || isNotNil(anonUserName);

  useEffect(() => {
    // @ts-expect-error unresolvable type error
    setStoredUserName(anonUserName);
  }, [anonUserName, setStoredUserName]);

  const anonInitials = useMemo(() => {
    return generateAnonInitials(anonUserName);
  }, [anonUserName]);

  const formattedContactsForTagging = useMemo((): ReactMentionsContact[] => {
    return formattedContacts(contactsForTeamTags);
  }, [contactsForTeamTags]);

  const deleteCommentErrorToast = () => {
    showToast({
      message: 'There was an error deleting the comment.',
      variant: 'error',
    });
  };

  const createCommentErrorToast = () => {
    showToast({
      message: 'Uh-oh. We had a problem creating your comment. Please try again.',
      variant: 'error',
    });
  };

  const updateCommentErrorToast = () => {
    showToast({
      message: 'Uh-oh. We had a problem updating your comment. Please try again.',
      variant: 'error',
    });
  };

  const [deleteAudienceComment] = useDeleteAudienceCommentMutation({
    onError: deleteCommentErrorToast,
  });

  const [deleteTeamComment] = useDeleteTeamCommentMutation({
    onError: deleteCommentErrorToast,
  });

  const [createAudienceComment] = useCreateAudienceCommentMutation({
    onError: createCommentErrorToast,
  });

  const [createAnonymousAudienceComment] = useCreateAnonymousAudienceCommentMutation({
    onError: createCommentErrorToast,
  });

  const [createTeamComment] = useCreateTeamCommentMutation({
    onError: createCommentErrorToast,
  });

  const [createAnonymousTeamComment] = useCreateAnonymousTeamCommentMutation({
    onError: createCommentErrorToast,
  });

  const [updateAudienceComment] = useUpdateAudienceCommentMutation({
    onError: updateCommentErrorToast,
  });

  const [updateTeamComment] = useUpdateTeamCommentMutation({
    onError: updateCommentErrorToast,
  });

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (commentingView === commentingViews.audience) {
        await deleteAudienceComment({
          variables: { input: { commentId } },
        });
      } else {
        await deleteTeamComment({
          variables: {
            input: { teamCommentId: commentId },
            orderBy: commentingSort,
          },
        });
      }
    },
    [commentingView, deleteAudienceComment, deleteTeamComment, commentingSort],
  );

  // The `createComment` handler accepts an optional `name` argument to be used
  // in the anonymous create mutations. It's used in the cases where `anonUserName`
  // hasn't been successfully saved in local storage before the mutation is executed,
  // so we manually pass the name to the handler.
  const createComment = useCallback(
    async (
      body: string,
      parentId: string | null,
      mediaTimestamp: number | null,
      mediaId: string,
      name?: Nilable<string>,
    ) => {
      if (commentingView === commentingViews.audience && !isAnonymousMedia && isNotNil(parentId)) {
        await createAudienceComment({
          variables: {
            input: { body, parentId, timecode: mediaTimestamp },
          },
          onCompleted: (data) => {
            onCompleted(data.createAudienceComment?.comment.id);
          },
        });
      } else if (
        commentingView === commentingViews.audience &&
        isAnonymousMedia &&
        isNotNil(parentId)
      ) {
        await createAnonymousAudienceComment({
          variables: {
            input: {
              body,
              name: name ?? anonUserName ?? 'Unavailable',
              parentId,
              timecode: mediaTimestamp,
            },
          },
          onCompleted: (data) => {
            onCompleted(data.createAnonymousAudienceComment?.anonymousComment.id);
          },
        });
      } else if (commentingView === commentingViews.team && !isAnonymousMedia) {
        await createTeamComment({
          variables: {
            input: { body, parentId, mediaId, mediaTimestamp },
            orderBy: commentingSort,
          },
          onCompleted: (data) => {
            // Dispatch message event to autocomplete comment checklist item
            window.postMessage({ action: TEAM_COMMENT_POSTED }, window.origin);
            onCompleted(data.createTeamComment?.teamComment.id);
          },
        });
      } else if (commentingView === commentingViews.team && isAnonymousMedia) {
        await createAnonymousTeamComment({
          variables: {
            input: {
              body,
              parentId,
              mediaId,
              mediaTimestamp,
              name: name ?? anonUserName ?? 'Unavailable',
            },
          },
          onCompleted: (data) => {
            onCompleted(data.createAnonymousTeamComment?.teamComment.id);
          },
        });
      } else {
        // Do nothing
      }
    },
    [
      commentingView,
      createAudienceComment,
      createTeamComment,
      createAnonymousTeamComment,
      createAnonymousAudienceComment,
      isAnonymousMedia,
      anonUserName,
      commentingSort,
    ],
  );

  const updateComment = useCallback(
    async (body: string, commentId: string) => {
      if (commentingView === commentingViews.audience) {
        await updateAudienceComment({
          variables: { input: { body, commentId } },
        });
      } else {
        await updateTeamComment({
          variables: { input: { body, teamCommentId: commentId } },
        });
      }
    },
    [commentingView, updateAudienceComment, updateTeamComment],
  );

  const value = useMemo(
    () => ({
      activeCommentId,
      commentingView,
      setCommentingView,
      deleteComment,
      createComment,
      updateComment,
      onCommentClick,
      isAnonymousMedia,
      anonUserName,
      setAnonUserName,
      shouldAllowReplies,
      anonInitials,
      formattedContactsForTagging,
      shouldUseLegacyPlayerApi,
      showCommentTabs,
    }),
    [
      activeCommentId,
      commentingView,
      setCommentingView,
      deleteComment,
      createComment,
      updateComment,
      onCommentClick,
      isAnonymousMedia,
      anonUserName,
      setAnonUserName,
      shouldAllowReplies,
      anonInitials,
      formattedContactsForTagging,
      shouldUseLegacyPlayerApi,
      showCommentTabs,
    ],
  );

  return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
};

export const useCommentContext = (): CommentContextData => {
  const commentContext = useContext(CommentContext);
  if (!commentContext) {
    throw new Error('useCommentContext must be used within a CommentContextProvider');
  }

  return commentContext;
};
